import { Typography } from "@mui/material";
import { memo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Dialog, FlexColBox, Link } from "../../../common/components";
import { useMoment } from "../../../common/hooks/useMoment";
import { palette, spacing } from "../../../styles/theme";
import { useThemeBreakpoints } from "../../../utils/themeUtils";
import StudioNavButton from "./StudioNavButton";
import { ProductLineType } from "../../../types";
import { useStudio } from "../../../context/Studio";
import ProductLineToDisplayTag from "./ProductLineToDisplayTag";
import HolidayWarning from "../../HolidayWarning";
import RuhmaImpexDeliveryWarning from "../../RuhmaImpexDeliveryWarning";

const StudioDeliveryModalButton = () => {
  const {
    designState: { shipping, productDefinition },
  } = useStudio();
  const { t } = useTranslation();
  const [modalOpen, setModalOpen] = useState(false);
  const { isMobile } = useThemeBreakpoints();

  const { weeksFromNow } = useMoment();

  const toggleModal = () => setModalOpen((prev) => !prev);

  const standardDelivery = shipping?.find(
    (shippingOption) => shippingOption.tier === "standard"
  );
  const expressDelivery = shipping?.find(
    (shippingOption) => shippingOption.tier === "express"
  );
  const isRuhmaImpexProduct = productDefinition?.factory === "RI";

  return (
    <>
      <StudioNavButton
        icon="acute"
        title={isMobile ? "" : t("studio.toolbar.estimatedDelivery.title")}
        onClick={toggleModal}
        iconSize={24}
      />
      <Dialog
        open={modalOpen}
        onClose={toggleModal}
        title={t("studio.toolbar.estimatedDelivery.title")}
        PaperProps={{ sx: { pb: { xs: undefined, md: "48px" } } }}
        content={
          <FlexColBox gap={2}>
            {/* Temporarily hidden - can be re-enabled when needed */}
            {/* {isRuhmaImpexProduct && <RuhmaImpexDeliveryWarning />} */}
            <HolidayWarning />
            <FlexColBox gap={spacing.groupingDesktop}>
              {standardDelivery && (
                <FlexColBox gap={spacing.xsDesktop}>
                  <Typography variant="actionBoldBigIcon">
                    {t("studio.toolbar.estimatedDelivery.standard")}
                  </Typography>
                  <Typography
                    variant="textRegular"
                    color={palette.grey.primary}
                  >
                    {t("studio.toolbar.estimatedDelivery.deliveryMessage", {
                      startWeek: standardDelivery?.minWeeks,
                      endWeek: standardDelivery?.maxWeeks,
                    })}
                    &nbsp;
                    <Typography
                      variant="textRegular"
                      fontWeight={700}
                      color={palette.grey.primary}
                    >{`${weeksFromNow(standardDelivery?.minWeeks)} - ${weeksFromNow(standardDelivery?.maxWeeks)}`}</Typography>
                  </Typography>
                </FlexColBox>
              )}

              {expressDelivery && (
                <FlexColBox gap={spacing.xsDesktop}>
                  <Typography variant="actionBoldBigIcon">
                    {t("studio.toolbar.estimatedDelivery.express")}
                  </Typography>
                  <Typography
                    variant="textRegular"
                    color={palette.grey.primary}
                  >
                    {t("studio.toolbar.estimatedDelivery.deliveryMessage", {
                      startWeek: expressDelivery?.minWeeks,
                      endWeek: expressDelivery?.maxWeeks,
                    })}
                    &nbsp;
                    <Typography
                      variant="textRegular"
                      fontWeight={700}
                      color={palette.grey.primary}
                    >{`${weeksFromNow(expressDelivery?.minWeeks)} - ${weeksFromNow(expressDelivery?.maxWeeks)}`}</Typography>
                  </Typography>
                </FlexColBox>
              )}

              <FlexColBox gap={spacing.xsDesktop}>
                <Typography variant="actionBoldBigIcon">
                  {t("studio.toolbar.estimatedDelivery.contactInfo.title")}
                </Typography>
                <Typography
                  variant="textRegular"
                  color={palette.grey.primary}
                  sx={{
                    "& > span": { display: "inline" },
                    "& a": { display: "inline" },
                  }}
                >
                  <Trans
                    i18nKey={
                      // will be used when we will launch the Eco and Classic lines
                      // isClassicOrEco
                      //   ? "studio.toolbar.estimatedDelivery.contactInfo.messageEcoAndClassics"
                      //   : "studio.toolbar.estimatedDelivery.contactInfo.messageHyperCustom"
                      "studio.toolbar.estimatedDelivery.contactInfo.messageEcoAndClassics"
                    }
                    components={[
                      <span key="0" />,
                      <Link
                        to="mailto:info@jameo.com"
                        style={{
                          fontSize: "14px",
                          display: "inline",
                        }}
                        key="1"
                      />,
                      <ProductLineToDisplayTag
                        key="2"
                        productLine={ProductLineType.ECO}
                      />,
                      <ProductLineToDisplayTag
                        key="3"
                        productLine={ProductLineType.CLASSIC}
                      />,
                    ]}
                  />
                </Typography>
              </FlexColBox>
            </FlexColBox>
          </FlexColBox>
        }
        action={undefined}
      />
    </>
  );
};

export default memo(StudioDeliveryModalButton);
